// src/components/ui-components/RoomCard.jsx

import { Box, Flex, Separator, Text, Tooltip, Button } from '@radix-ui/themes';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaBell } from 'react-icons/fa';
import diaper from '../../assets/diaper.svg';
import diaperDisabled from '../../assets/diaper-disable.svg';
import medication from '../../assets/medication.svg';
import medicationDisabled from '../../assets/medication-disable.svg';
import bedManagement from '../../assets/bed-managent.svg';
import nurseCalling from '../../assets/nuses-calling.svg';
import nurseCallingDisabled from '../../assets/nurses-calling-disable.svg';
import patient from '../../assets/patient.svg';
import rolling from '../../assets/rolling.svg';
import rollingDisabled from '../../assets/rolling-disable.svg';
import wakeupDisabled from '../../assets/wakeup-disable.svg';
import batteryFull from '../../assets/battery-full.svg';
import batteryHalf from '../../assets/half_battery.svg';
import batteryLow from '../../assets/low-battery.svg';
import batteryEmpty from '../../assets/battery_empy.svg';
import bluetoothConnected from '../../assets/bluetooth-connected.svg';
import bluetoothLow from '../../assets/signal-low.svg';
import vaccant from '../../assets/vacant.svg';
import occupied from '../../assets/bed-managent.svg';
import hold from '../../assets/hold.svg';
import GoogleFormLinksDialog from './GoogleFormLinksDialog';
import RoomForm from './RoomForm';

// *** Update props to accept new handlers, sensor status, and medication state ***
const RoomCard = ({ 
  bedData, 
  refreshBeds, 
  bedAlarms = [], 
  isOnHold = false,
  sensorStatus = { batPercent: 0, btPercent: 0 },
  medicationState = { isActive: false, medicationReminderId: null },
  diaperState = false,
  onConfirmAlarm, 
  onCancelAlarm,
  onMedicationIconClick,
  onAcceptExtension,
  onCancelExtension,
  onManagementOpenChange
}) => {
  const roleId = localStorage.getItem('roleId');
  const navigate = useNavigate();

  // Get the highest priority alarm (first in sorted array)
  const currentAlarm = bedAlarms.length > 0 ? bedAlarms[0] : null;

  // *** Determine if the current alarm is a hold extension ***
  const isExtensionAlarm = currentAlarm ? currentAlarm.isExtension : false;

  // Helper function to get battery icon based on percentage
  const getBatteryIcon = (percentage) => {
    if (percentage === 0) return batteryEmpty;
    if (percentage <= 20) return batteryLow;
    if (percentage <= 70) return batteryHalf;
    return batteryFull; // 70-100%
  };

  // Helper function to get bluetooth icon based on percentage
  const getBluetoothIcon = (percentage) => {
    if (percentage < 10) return bluetoothLow;
    return bluetoothConnected;
  };

  // Helper function to get icon based on enabled state
  const getIconForFeature = (isEnabled, enabledIcon, disabledIcon) => {
    return isEnabled ? enabledIcon : disabledIcon;
  };

  // NEW: Enhanced medication click handler
  const handleMedicationClick = () => {
    if (medicationState.isActive) {
      // If medication is in active state (orange), show popup
      console.log('Medication icon clicked - showing popup for active medication');
      onMedicationIconClick();
    } else if (bedData.patientId) {
      // If patient assigned but no active medication, navigate to medication page
      navigate(`/medication?patientToken=${bedData.patientToken}&bedId=${bedData.id}`);
    } else {
      console.log('No patient assigned to this bed.');
    }
  };

  const getMedicationIconColor = () => {
    if (medicationState.isActive) {
      return 'orange'; // Active medication state
    } else if (bedData.patientId) {
      return 'green'; // Patient assigned, normal state
    } else {
      return 'grey'; // No patient assigned
    }
  };

  // NEW: Determine diaper icon color based on state
  const getDiaperIconColor = () => {
    if (diaperState) {
      return 'red'; // Diaper alarm confirmed state
    } else if (bedData.isWetSafeEnabled) {
      return 'green'; // Enabled, normal state
    } else {
      return 'grey'; // Disabled
    }
  };

  const defaultIcons1 = [
    { 
      logo: getIconForFeature(bedData.isWetSafeEnabled, diaper, diaperDisabled), 
      label: 'Diaper', 
      key: 'diaper' 
    },
    { 
      logo: bedData.patientId ? medication : medicationDisabled, 
      label: 'Medication', 
      key: 'medication',
      onClick: handleMedicationClick // Add click handler
    },
    { 
      logo: bedManagement, 
      label: 'Bed Status', 
      key: 'room_stat' 
    },
  ];
  
  const defaultIcons2 = [
    { 
      logo: getIconForFeature(bedData.isNurseCallEnabled, nurseCalling, nurseCallingDisabled), 
      label: 'Nurse Calling', 
      key: 'nurse_calling' 
    },
    { 
      logo: getIconForFeature(bedData.isWakeupEnabled, patient, wakeupDisabled), 
      label: 'wake up', 
      key: 'wake_up' 
    },
    { 
      logo: getIconForFeature(bedData.isRollingEnabled, rolling, rollingDisabled), 
      label: 'Rolling', 
      key: 'rolling' 
    },
  ];

  // Updated room status function to handle alarmStatus from bed data
  const getRoomStatus = () => {
    // Check if bed has alarmStatus 'OnHold' - this overrides other states
    if (bedData.alarmStatus === 'OnHold') {
      return 'orange'; // Hold state from bed data
    }
    
    // Check if bed is on hold from SignalR alert
    if (isOnHold) {
      return 'orange'; // Hold state from SignalR
    }
    
    // Legacy hold check (keeping for backward compatibility)
    if (bedData.isHold) {
      return 'orange'; // Original hold logic
    }
    
    // Normal states
    if (bedData.patientId) {
      return 'green'; // Occupied (patient assigned)
    }
    
    return 'blue'; // Vacant (no patient)
  };

  const cardData = {
    room_id: bedData.roomNumber,
    diaper: getDiaperIconColor(),
    medication: getMedicationIconColor(), // Use new function for medication color
    room_stat: getRoomStatus(),
    nurse_calling: bedData.isNurseCallEnabled ? 'green' : 'grey',
    rolling: bedData.isRollingEnabled ? 'green' : 'grey',
    wake_up: bedData.isWakeupEnabled ? 'green' : 'grey',
    isDischarge: !bedData.isActive,
  };

  // Click handler for the standard "Confirm" button
  const handleConfirmClick = () => {
    if (currentAlarm && onConfirmAlarm) {
      onConfirmAlarm();
    }
  };

  // Click handler for the standard "Cancel" button
  const handleCancelClick = () => {
    if (currentAlarm && onCancelAlarm) {
      onCancelAlarm();
    }
  };

  // *** NEW: Click handler for the "Accept" extension button ***
  const handleAcceptClick = () => {
    if (currentAlarm && onAcceptExtension) {
      onAcceptExtension();
    }
  };

  // *** NEW: Click handler for the "Cancel" extension button ***
  const handleExtensionCancelClick = () => {
    if (currentAlarm && onCancelExtension) {
      onCancelExtension();
    }
  };
 const getAlarmIcon = (alarm) => {
  // Use alarmName from the alarm object or bedData
  const alarmName = alarm?.alarmName || alarm?.payload?.alarmName;
  
  if (!alarmName) return null;
  
  // Map alarm names to corresponding icons
  switch (alarmName.toLowerCase()) {
    case 'medication':
      return medication;
    case 'diaper':
    case 'wetsafe':
    case 'wet safe':
      return diaper;
    case 'nurse call':
    case 'nurse calling':
    case 'nursecall':
      return nurseCalling;
    case 'rolling':
      return rolling;
    case 'wakeup':
    case 'wake up':
      return patient;
    case 'patient out':
    case 'patientout':
      return patient;
    case 'food call':
    case 'foodcall':
      return patient; // You might want a specific food icon
    case 'security call':
    case 'securitycall':
      return patient; // You might want a specific security icon
    case 'laundry call':
    case 'laundrycall':
      return patient; // You might want a specific laundry icon
    default:
      return null; // Return null instead of FaBell to use FaBell as fallback
  }
};

 return (
  <Box 
    className={`w-full shadow-md rounded-2xl ${currentAlarm ? 'animate-pulse-enhanced' : ''}`}
  >
    <style>
      {`
        @keyframes pulse-enhanced {
          0% {
            box-shadow: 0 0 0 0 rgba(255, 0, 0, 0.7), 0 0 10px 2px rgba(255, 0, 0, 0.5);
          }
          50% {
            box-shadow: 0 0 0 12px rgba(255, 0, 0, 0.2), 0 0 20px 4px rgba(255, 0, 0, 0.8);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(255, 0, 0, 0), 0 0 10px 2px rgba(255, 0, 0, 0.5);
          }
        }
        .animate-pulse-enhanced {
          animation: pulse-enhanced 1.2s ease-in-out infinite;
        }
      `}
    </style>
    <div className="rounded-2xl bg-[#f6f5fa]">
      <Flex direction="column" gap="0" className="rounded-2xl">
        {/* Header - Room Number Section (unchanged) */}
        <Flex
          gap="3"
          align="center"
          justify="between"
          className="bg-[#efeeee] px-3 py-2 rounded-t-2xl"
        >
          <Flex gap="2" align="center">
            <Text as="div" size={{ initial: '4', sm: '5' }} weight="bold">
              Room: {cardData.room_id || '1204'}
            </Text>
          </Flex>
          <Box>
            <RoomForm 
              roleId={roleId} 
              bedData={bedData} 
              refreshBeds={refreshBeds} 
              onOpenChange={onManagementOpenChange}
            />
          </Box>
        </Flex>
        
        {/* Body Section - This will change color during alarms */}
        <Flex 
          direction="column"
          className="rounded-b-2xl transition-colors duration-300 ease-in-out"
          style={{ 
            backgroundColor: currentAlarm ? currentAlarm.color : '#f6f5fa',
            boxShadow: currentAlarm ? 'inset 0 0 0 3px white' : 'none'
          }}
        >
          {currentAlarm ? (
            // *** ALARM STATE UI - Fixed height to match normal state ***
            <Flex 
              direction="column" 
              justify="center" 
              align="center" 
              className="px-3"
              style={{ minHeight: '140px' }} // Fixed height to match normal card height
            >
              {/* Show alarm icon if available - smaller size */}
              {getAlarmIcon(currentAlarm) ? (
                <Box className="mb-2">
                  <img 
                    src={getAlarmIcon(currentAlarm)} 
                    alt="Alarm Icon" 
                    width={32} 
                    height={32}
                    className="filter brightness-0 invert" // Makes icon white
                  />
                </Box>
              ) : (
                <Box className="mb-2">
                  <FaBell className="text-white text-2xl" />
                </Box>
              )}
              
              {isExtensionAlarm ? (
                // *** UI for Hold Extension Alarm ***
                <>
                  <Text weight="bold" size="4" className="text-white mb-3 text-center">
                    Extend {currentAlarm.alarmName}?
                  </Text>
                  <Flex gap="3">
                    <Button 
                      size="2" 
                      onClick={handleAcceptClick}
                      className="bg-green-600 hover:bg-green-700 text-white border-0 font-semibold shadow-lg"
                      data-bed-id={bedData.id}
                      style={{ backgroundColor: '#16a34a', color: 'white' }}
                    >
                      Accept
                    </Button>
                    <Button 
                      size="2" 
                      onClick={handleExtensionCancelClick}
                      className="bg-red-600 hover:bg-red-700 text-white border-0 font-semibold shadow-lg"
                      data-bed-id={bedData.id}
                      style={{ backgroundColor: '#dc2626', color: 'white' }}
                    >
                      Cancel
                    </Button>
                  </Flex>
                </>
              ) : (
                // *** UI for Standard Alarm ***
                <>
                  <Text weight="bold" size="4" className="text-white mb-3 text-center">
                    {currentAlarm.alarmName}
                  </Text>
                  <Flex gap="3">
                    <Button 
                      size="2" 
                      onClick={handleConfirmClick}
                      className="bg-green-600 hover:bg-green-700 text-white border-0 font-semibold shadow-lg"
                      data-bed-id={bedData.id}
                      style={{ backgroundColor: '#16a34a', color: 'white' }}
                    >
                      Confirm
                    </Button>
                    <Button 
                      size="2" 
                      onClick={handleCancelClick}
                      className="bg-red-600 hover:bg-red-700 text-white border-0 font-semibold shadow-lg"
                      data-bed-id={bedData.id}
                      style={{ backgroundColor: '#dc2626', color: 'white' }}
                    >
                      Cancel
                    </Button>
                  </Flex>
                </>
              )}
            </Flex>
          ) : (
            // *** NORMAL STATE UI (no alarm) ***
            <>
              <Flex className="px-3 py-2" justify="between" align="center">
                <Flex gap="2" className="min-w-0 flex-shrink-0">
                  {defaultIcons1.map((alarm) => (
                    <Tooltip content={alarm.label} key={alarm.key}>
                      <Box
                        width="40px"
                        height="40px"
                        onClick={alarm.onClick || undefined}
                        className={`flex items-center justify-center shadow-md p-2 rounded-2xl flex-shrink-0 cursor-pointer transition-colors duration-300 ease-in-out ${
                          cardData[alarm.key]
                            ? cardData[alarm.key] === 'green'
                              ? 'bg-gradient-to-b from-[#29b576] to-[#22a067]'
                              : cardData[alarm.key] === 'orange'
                              ? 'bg-gradient-to-b from-[#ffa200] to-[#ff6f00]'
                              : cardData[alarm.key] === 'red'
                              ? 'bg-gradient-to-b from-[#ff2300] to-[#b21800]'
                              : cardData[alarm.key] === 'grey'
                              ? 'bg-gradient-to-b from-[#c3c3c3] to-[#b6b6b6]'
                              : cardData[alarm.key] === 'blue' && 'bg-gradient-to-b from-[#11c8fe] to-[#008bb4]'
                            : 'bg-gradient-to-b from-[#29b576] to-[#22a067]'
                        }`}
                      >
                        {alarm.logo ? (
                          alarm.key === 'room_stat' ? (
                            // Updated room status icon logic
                            cardData[alarm.key] === 'orange' ? ( // Hold state (from alarmStatus or isOnHold)
                              <img className="scale-[.8] -mt-1" src={hold} alt="Hold" />
                            ) : cardData[alarm.key] === 'blue' ? (
                              <img src={vaccant} alt="Vacant" />
                            ) : cardData[alarm.key] === 'green' ? (
                              <img className="scale-[1.2] mt-1" src={occupied} alt="Occupied" />
                            ) : null
                          ) : (
                            <img src={alarm.logo} alt={alarm.label} />
                          )
                        ) : (
                          alarm.icon
                        )}
                      </Box>
                    </Tooltip>
                  ))}
                </Flex>
                <Flex className="flex-shrink-0" gap="1" direction="column" align="center" justify="center">
                  <Text size="2">Sensor</Text>
                  <Flex gap="2">
                    <Tooltip content={`Battery: ${sensorStatus.batPercent}%`}>
                      <img 
                        src={getBatteryIcon(sensorStatus.batPercent)} 
                        alt={`Battery ${sensorStatus.batPercent}%`} 
                        width={20} 
                      />
                    </Tooltip>
                    <Tooltip content={`Bluetooth: ${sensorStatus.btPercent}%`}>
                      <img 
                        src={getBluetoothIcon(sensorStatus.btPercent)} 
                        alt={`Bluetooth ${sensorStatus.btPercent}%`} 
                        width={20} 
                      />
                    </Tooltip>
                  </Flex>
                </Flex>
              </Flex>
              
              <Separator className="my-2" size="4" orientation="horizontal" />
              
              <Flex className="px-3 py-2" justify="between" align="center">
                <Flex gap="2" className="min-w-0 flex-shrink-0">
                  {defaultIcons2.map((alarm) => (
                    <Tooltip content={alarm.label} key={alarm.key}>
                      <Box
                        width="40px"
                        height="40px"
                        className={`flex items-center justify-center p-2 shadow-md rounded-2xl flex-shrink-0 transition-colors duration-300 ease-in-out ${
                          cardData[alarm.key]
                            ? cardData[alarm.key] === 'green'
                              ? 'bg-gradient-to-b from-[#29b576] to-[#22a067]'
                              : cardData[alarm.key] === 'orange'
                              ? 'bg-gradient-to-b from-[#ffa200] to-[#ff6f00]'
                              : cardData[alarm.key] === 'red'
                              ? 'bg-gradient-to-b from-[#ff2300] to-[#b21800]'
                              : cardData[alarm.key] === 'grey'
                              ? 'bg-gradient-to-b from-[#c3c3c3] to-[#b6b6b6]'
                              : cardData[alarm.key] === 'blue' && 'bg-gradient-to-b from-[#11c8fe] to-[#008bb4]'
                            : 'bg-gradient-to-b from-[#29b576] to-[#22a067]'
                        }`}
                      >
                        {alarm.logo ? (
                          <img width={24} src={alarm.logo} alt={alarm.label} />
                        ) : (
                          alarm.icon
                        )}
                      </Box>
                    </Tooltip>
                  ))}
                </Flex>
                <Box className="flex-shrink-0">
                  <GoogleFormLinksDialog patientId={bedData.patientId} />
                </Box>
              </Flex>
            </>
          )}
        </Flex>
      </Flex>
    </div>
  </Box>
);
};

export default RoomCard;
